import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, type ActivityInfo, type FavoriteInfo, type ReviewDue } from "../api";
import { useAuth } from "../auth";
import { AccentBlock, Badge, Card, Num } from "../components/primitives";
import { SkeletonText } from "../components/Skeleton";
import { STATUS_LABELS } from "./projekt/ProjektPage";
import { formatDate, formatWhen } from "../time";

const ROLE_LABELS: Record<string, string> = {
  admin: "administratör",
  member: "medlem",
};

type Meta = {
  application: string;
  version: string;
  auth_mode: string;
};

type ApiState = { status: "loading" } | { status: "ready"; meta: Meta } | { status: "unreachable" };

function useApiMeta(): ApiState {
  const [state, setState] = useState<ApiState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();

    // Relative URL by design: Vite proxies it in development, FastAPI serves it in production.
    fetch("/api/v1/meta", { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API svarade ${response.status}`);
        }
        return response.json() as Promise<Meta>;
      })
      .then((meta) => setState({ status: "ready", meta }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setState({ status: "unreachable" });
      });

    return () => controller.abort();
  }, []);

  return state;
}

export function StartPage() {
  const api = useApiMeta();
  const { auth } = useAuth();

  return (
    <>
      <Card hero>
        <p className="nb-overline">Nordiska Brand AB</p>
        <h1 className="nb-title mt-s4 text-hero text-ink">Noteshelf</h1>
        <p className="mt-s4 max-w-[52ch] text-body text-muted">
          Företagets wiki, anteckningsbok och projektpärm. Designgrunden är på plats — innehållet
          byggs arbetspaket för arbetspaket.
        </p>
      </Card>

      <Card>
        <h2 className="nb-overline">Systemstatus</h2>
        <div className="mt-s5" aria-live="polite">
          {api.status === "loading" && <SkeletonText lines={2} />}

          {api.status === "ready" && (
            <AccentBlock className="grid gap-s3">
              <p className="text-body text-ink">
                API <Num>v{api.meta.version}</Num> svarar. Inloggningsläge:{" "}
                <Num>{api.meta.auth_mode}</Num>.
              </p>
              {auth.status === "signed_in" && (
                <p className="text-body text-ink">
                  Inloggad som {auth.user.display_name} —{" "}
                  {ROLE_LABELS[auth.user.role] ?? auth.user.role}.
                </p>
              )}
              <p>
                <Badge status="pass">Uppkopplad</Badge>
              </p>
            </AccentBlock>
          )}

          {api.status === "unreachable" && (
            <AccentBlock className="grid gap-s3">
              <p className="text-body text-ink">
                API:t svarar inte. Kontrollera att devstacken är igång.
              </p>
              <p>
                <Badge status="fail">Ingen kontakt</Badge>
              </p>
            </AccentBlock>
          )}
        </div>
      </Card>

      <ReviewDueCard />
      <ActivityCard />
      <FavoritesCard />
    </>
  );
}

/** Governed pages due for review, overdue first (WP0.9). Hidden when nothing is due. */
function ReviewDueCard() {
  const navigate = useNavigate();
  const [due, setDue] = useState<ReviewDue[] | null>(null);

  useEffect(() => {
    void api.reviewDue().then(setDue);
  }, []);

  if (!due || due.length === 0) {
    return null;
  }

  return (
    <Card>
      <h2 className="nb-overline">Dokument att granska</h2>
      <ul className="mt-s5 grid gap-s2">
        {due.map((item) => (
          <li key={item.page_id}>
            <button
              type="button"
              className="flex w-full items-baseline gap-s4 rounded-control px-s3 py-s2 text-left transition-[background] duration-control ease-nb hover:bg-surface-2"
              onClick={() => navigate(`/sidor/${item.page_id}`)}
            >
              <span className="min-w-0 flex-1 truncate text-body text-ink">{item.title}</span>
              <span
                className={`shrink-0 text-small ${item.overdue ? "text-fail-text" : "text-muted"}`}
              >
                {item.overdue ? "försutten" : "granskas"} {formatDate(item.review_due_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** The user's own trail, read from the audit log: what did I touch lately, newest first. */
function ActivityCard() {
  const navigate = useNavigate();
  const [activity, setActivity] = useState<ActivityInfo[] | null>(null);

  useEffect(() => {
    void api.myActivity().then(setActivity);
  }, []);

  const open = (item: ActivityInfo) => {
    if (item.kind === "page") {
      navigate(`/sidor/${item.target_id}`);
    } else if (item.kind === "project") {
      navigate(`/projekt/${item.target_id}`);
    } else {
      navigate("/strommen");
    }
  };

  return (
    <Card>
      <h2 className="nb-overline">Din senaste aktivitet</h2>
      <div className="mt-s5">
        {activity === null ? (
          <SkeletonText lines={3} />
        ) : activity.length === 0 ? (
          <p className="text-body text-muted">
            Ingenting ännu. Det du skapar, redigerar och fångar hamnar här.
          </p>
        ) : (
          <ul className="grid gap-s2">
            {activity.map((item) => (
              <li key={`${item.kind}:${item.target_id}`}>
                <button
                  type="button"
                  className="flex w-full items-baseline gap-s4 rounded-control px-s3 py-s2 text-left transition-[background] duration-control ease-nb hover:bg-surface-2"
                  onClick={() => open(item)}
                >
                  <span className="min-w-0 flex-1 truncate text-body text-ink">
                    <span className="text-muted">{item.verb}</span> {item.title}
                  </span>
                  <time dateTime={item.occurred_at} className="nb-num shrink-0 text-small text-muted">
                    {formatWhen(item.occurred_at)}
                  </time>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/** Pinned pages and projects in the user's own order — the quick jumps. */
function FavoritesCard() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<FavoriteInfo[] | null>(null);

  useEffect(() => {
    void api.favorites().then(setFavorites);
  }, []);

  const move = useCallback(
    (index: number, delta: -1 | 1) => {
      setFavorites((current) => {
        if (!current) {
          return current;
        }
        const target = index + delta;
        if (target < 0 || target >= current.length) {
          return current;
        }
        const next = [...current];
        [next[index], next[target]] = [next[target], next[index]];
        void api.orderFavorites(next.map((f) => ({ kind: f.kind, target_id: f.target_id })));
        return next;
      });
    },
    [],
  );

  const remove = useCallback((favorite: FavoriteInfo) => {
    setFavorites((current) =>
      current ? current.filter((f) => f.target_id !== favorite.target_id) : current,
    );
    void api.removeFavorite(favorite.kind, favorite.target_id);
  }, []);

  return (
    <Card>
      <h2 className="nb-overline">Favoriter</h2>
      <div className="mt-s5">
        {favorites === null ? (
          <SkeletonText lines={2} />
        ) : favorites.length === 0 ? (
          <p className="text-body text-muted">
            Inga favoriter ännu. Stjärnmärk en sida eller ett projekt (★ uppe vid titeln) så
            hamnar den här, i den ordning du själv väljer.
          </p>
        ) : (
          <ul className="grid gap-s2">
            {favorites.map((favorite, index) => (
              <li key={`${favorite.kind}:${favorite.target_id}`} className="flex items-center gap-s2">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-baseline gap-s4 rounded-control px-s3 py-s2 text-left transition-[background] duration-control ease-nb hover:bg-surface-2"
                  onClick={() =>
                    navigate(
                      favorite.kind === "page"
                        ? `/sidor/${favorite.target_id}`
                        : `/projekt/${favorite.target_id}`,
                    )
                  }
                >
                  <span className="min-w-0 flex-1 truncate text-body text-ink">
                    {favorite.title}
                  </span>
                  {favorite.hint && (
                    <span className="shrink-0 text-small text-muted">
                      {favorite.kind === "project"
                        ? (STATUS_LABELS[favorite.hint] ?? favorite.hint)
                        : favorite.hint}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  aria-label={`Flytta upp ${favorite.title}`}
                  className="rounded-control px-s2 text-small text-muted hover:text-ink disabled:opacity-30"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`Flytta ner ${favorite.title}`}
                  className="rounded-control px-s2 text-small text-muted hover:text-ink disabled:opacity-30"
                  disabled={index === favorites.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  aria-label={`Ta bort favoriten ${favorite.title}`}
                  className="rounded-control px-s2 text-small text-muted hover:text-ink"
                  onClick={() => remove(favorite)}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
